import { z } from "zod";
import { collapseWhitespace } from "../text.js";
import { bottleStatusSchema } from "./lot.js";
/**
 * Moving bottles between shelves.
 *
 * The request names bottles by id rather than saying "three of the Ripassa
 * from shelf 2". Bottles within a lot are physically indistinguishable, so a
 * person moves a count and the interface picks which ids that means — but what
 * is written to the history has to be the actual objects, or the event log
 * stops being able to answer where a given bottle has been.
 *
 * A move is addressed to the cellar rather than to a wine. It is a fact about
 * bottles, and emptying a shelf into another cabinet does not care which wines
 * are standing on it.
 */
export const moveBottlesRequestSchema = z.object({
    /** 240 is the same ceiling a lot has: two pallets of bottles at once. */
    bottleIds: z.array(z.string().min(1)).min(1).max(240),
    toShelfId: z.string().min(1),
});
/**
 * What the move did, and how full the destination now is.
 *
 * The occupancy comes back with the answer because capacity is advisory —
 * project-scope.md: it warns, it does not refuse. The server is therefore the
 * wrong place to turn a full shelf into a 409, and the client is the wrong
 * place to count what is on a shelf it cannot see. So the write happens and
 * the truth about the shelf comes back with it, for the interface to say out
 * loud.
 */
export const moveBottlesResponseSchema = z.object({
    moved: z.number().int(),
    /** Bottles already on the destination. Moved nowhere, and no event written
     *  for them: a move from a shelf to itself did not happen. */
    alreadyThere: z.number().int(),
    /** Bottles standing on the destination shelf now, and what it says it holds. */
    shelfCount: z.number().int(),
    shelfCapacity: z.number().int().nullable(),
});
/** A calendar date with no time in it, as `YYYY-MM-DD`. */
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * Drinking a bottle: the date, and what it tasted like.
 *
 * Two taps plus the note, per the plan, so everything here except the date is
 * optional and the date defaults to today in the interface. A bottle opened on
 * Saturday and written up on Sunday is recorded as Saturday — when it happened
 * in the world is not when it was written down, and the drinking history cares
 * about the former.
 *
 * The score is optional and most bottles will not carry one — a bottle can be
 * worth writing up without being worth grading. Its scale is not sent: it is
 * the cellar's, recorded on the cellar row, and a client that could name the
 * scale could file a twenty-point score as a hundred-point one. The range is
 * checked against the cellar by the server for the same reason.
 */
export const consumeBottleRequestSchema = z.object({
    drunkOn: dateOnly,
    note: z
        .string()
        .transform(collapseWhitespace)
        .pipe(z.string().max(4000))
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .default(null),
    /** A corked bottle is still drunk, but says nothing about the wine. */
    isFaulty: z.boolean().default(false),
    /**
     * What you thought of this bottle, on the cellar's scale. Null is the normal
     * case. The ceiling here is only a sanity bound — the real one is the
     * cellar's `tastingScaleMax`, which the server checks against, because only
     * it knows what scale the number is on.
     */
    score: z.number().min(0).max(100).nullable().default(null),
});
/**
 * What happened, and what undoing it would name.
 *
 * The event id comes back because a two-tap consumption is a two-tap mistake:
 * the interface offers *ångra* immediately, and it has to be able to say which
 * act it is undoing. Undo reverts an event rather than a bottle, because a
 * bottle can be wrong in more than one way and only the event knows which one
 * just happened.
 */
export const bottleActionResponseSchema = z.object({
    eventId: z.string(),
    bottleId: z.string(),
    status: bottleStatusSchema,
    /** Where it stands afterwards. Null once it has left the cellar. */
    shelfName: z.string().nullable(),
    storageName: z.string().nullable(),
});
/**
 * A bottle that leaves the cellar without being drunk.
 *
 * Four endings rather than four endpoints, because they differ only in what
 * the record ends up saying: the act is the same act, and a separate route per
 * status would be four copies of one function that drift the first time the
 * shape changes. What must not be merged is this with `Away` — a bottle at a
 * dinner is still owned and still counts in the inventory, and the two are
 * kept apart in the contract as well as in the interface.
 *
 * Broken and Lost take a note as readily as Gifted and Sold do. "dropped on
 * the cellar steps" is worth as much in fifteen years as "to Anna, for the
 * christening", and a field that only some of the endings carried would be a
 * judgement about which losses are worth explaining.
 */
export const bottleDepartureSchema = z.enum(["Gifted", "Sold", "Lost", "Broken"]);
/**
 * Free text about one act on one bottle: where it went, or how it went.
 *
 * Shorter than a tasting note by an order of magnitude, and deliberately so —
 * this is a line about an event, not an account of a wine.
 */
export const eventNoteSchema = z
    .string()
    .transform(collapseWhitespace)
    .pipe(z.string().max(500))
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .default(null);
export const departBottleRequestSchema = z.object({
    kind: bottleDepartureSchema,
    /** When it happened in the world, as with a consumption. */
    leftOn: dateOnly,
    note: eventNoteSchema,
});
/**
 * Taking a bottle out of the cellar without giving it up.
 *
 * The shelf it came from is not sent: the server reads it off the bottle,
 * which is the only place that knows it, and remembering it is the whole point
 * of the status. A client that supplied the shelf could supply the wrong one
 * and make the return put the bottle somewhere it never stood.
 */
export const takeBottleAwayRequestSchema = z.object({
    takenOn: dateOnly,
    /** `to Anna's, for the dinner on Saturday`. Optional, and usually given. */
    note: eventNoteSchema,
});
/**
 * Bringing one back.
 *
 * `toShelfId` is null in the ordinary case, and the bottle goes back where it
 * came from — project-scope.md asks for returning to be one action rather than
 * a re-filing decision. It is supplied only when there is nothing to go back
 * to, which happens when the shelf was emptied and removed while the bottle
 * was away: the shelf reference goes null, and the bottle needs somewhere to
 * be put rather than a refusal.
 */
export const returnBottleRequestSchema = z.object({
    returnedOn: dateOnly,
    toShelfId: z.string().min(1).nullable().default(null),
});
/**
 * Correcting what was written down about a departure — never that it happened.
 *
 * `project-scope.md`, 2026-09-11: a bottle drunk on the 4th and entered as the
 * 14th did not leave on the 14th. That is a typing mistake, not an event, and
 * undoing the departure only to record it again would put two of them in a
 * history kept precisely so it can be trusted. The compensating event is still
 * the only way to say *it did not leave at all*.
 *
 * The note means two different things depending on how the bottle went, and the
 * server files it accordingly: for a bottle that was drunk it is the tasting
 * note — the words, the score and whether it was corked — and for one that was
 * given, sold, lost or broken it is the event's own note, `to Anna, for the
 * christening`. The score and the fault only apply to the first: a bottle that
 * was given away was not tasted by anybody here.
 *
 * **A date may also be given where there was none.** Thirty-five bottles came
 * in from the sheet as gone on a day nobody had written down, and they carry a
 * status with no event at all. Remembering later that it was New Year's Eve is
 * a fact the owner asserts, and it is written as the event it always was.
 */
export const correctDepartureRequestSchema = z.object({
    occurredAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    note: eventNoteSchema,
    /** Ignored for a bottle that was not drunk. */
    score: z.number().min(0).max(1000).nullable().default(null),
    isFaulty: z.boolean().default(false),
});
//# sourceMappingURL=bottle.js.map